import React, { useState, useRef, useEffect } from 'react';
import { 
  Button, 
  IconButton, 
  Box,
  styled,
  CircularProgress
} from '@mui/material';
import { 
  CheckCircle,
  ChevronLeft as ChevronLeftIcon, 
  ChevronRight as ChevronRightIcon 
} from '@mui/icons-material';
import { defaultEndTime, defaultMaxGroups, defaultMaxPeople, defaultMaxPeoplePerLocation, defaultSlotSize, defaultStartTime } from 'shared';
import { zonedTimeToUtc } from 'date-fns-tz';
import dayjs from 'dayjs';

// Custom styled components
const ScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  gap: theme.spacing(1),
  paddingTop: 0,
  padding: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none'
}));

export const TimeButton = styled(Button)(({ theme }) => ({
  minWidth: '110px',
  height: '45px',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  position: 'relative',
  bgcolor: "#FFF"
}));

const ScrollButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 1,
}));

// Generate time slots e.g. from 09:30 to 17:00 in intervals of slotSize
export const generateTimeSlots = ({startTime, endTime, slotSize, disabledTimes}) => {
    const slots = [];
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    const startDate = new Date();
    startDate.setHours(startHour, startMinute, 0);

    const endDate = new Date();
    endDate.setHours(endHour, endMinute, 0);

    while (startDate < endDate) {
        const time = startDate.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
        });

        if (!disabledTimes?.includes(time)) {
          slots.push(time);
        }
      
        startDate.setMinutes(startDate.getMinutes() + slotSize);
    }
   
    return slots;
};


const TimeSlotSelector = ({
    selectedDate,
    timeZone,
    selectedTime,
    setSelectedTime,
    startTime,
    endTime,
    slotSize,
    disabledTimes,
    slotInfo,
    isLoading
}) => {

  const sizeOfSlot = slotSize || defaultSlotSize;
  const timeSlots = generateTimeSlots({
    startTime: startTime || defaultStartTime,
    endTime: endTime || defaultEndTime,
    slotSize: sizeOfSlot,
    disabledTimes: disabledTimes || []
  });

  const scrollRef = useRef(null);
  const [scrollButtons, setScrollButtons] = useState({
    left: false,
    right: true
  });

  const scrollLeft = () => {
    scrollRef.current.scrollBy({ left: -150, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollRef.current.scrollBy({ left: 150, behavior: 'smooth' });
  };

  const handleScroll = () => {
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setScrollButtons({
      left: scrollLeft > 0,
      right: scrollLeft + clientWidth < scrollWidth
    });
  };

  return (
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>
        {isLoading ? 
          <CircularProgress /> :
          <React.Fragment>
            {scrollButtons.left && (
              <ScrollButton onClick={scrollLeft} sx={{ left: -20 }}>
                <ChevronLeftIcon />
              </ScrollButton>
            )}
            <ScrollContainer ref={scrollRef} onScroll={handleScroll}>
              {timeSlots.filter(slot => !selectedDate ||
                zonedTimeToUtc(`${selectedDate.format('YYYY-MM-DD')}T${slot}:00`, timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone).getTime() > Date.now() - sizeOfSlot * 60000
              ).map((time) => (
                <Box sx={{position: 'relative'}} key={time}>
                  <TimeButton
                    key={time}
                    variant="outlined"
                    className={selectedTime === time ? 'selected' : ''}
                    onClick={() => setSelectedTime(time)}
                    sx={{
                      border: selectedTime === time ? '1px solid' : '1px solid #ccc',
                    }}
                  >
                    {time}
                    {selectedTime === time && <CheckCircle fontSize='small' sx={{position: 'absolute', top: 0, right: 0}} />}   
                  </TimeButton>
                    {slotInfo && 
                      <Box sx={{ pt:1, fontSize: '12px', textAlign: 'center'}}>
                    {slotInfo(time).people}/{defaultMaxPeople} people<br />
                    {slotInfo(time).groups}/{defaultMaxGroups} groups
                    </Box>
                  }
                </Box>
              ))}
            </ScrollContainer>
            {scrollButtons.right && (
              <ScrollButton onClick={scrollRight} sx={{ right: -20 }}>
                <ChevronRightIcon />
              </ScrollButton>
              )}
        </React.Fragment>
      }
    </Box>
  );
};

export const useSlotsInfo = (ticketLocation, startDate, excludeSessionId) => {
  const [slotsInfo, setSlotsInfo] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  // Booking management supplies a native Date; checkout supplies a Day.js value.
  const visitDate = startDate == null ? undefined : dayjs(startDate).format('YYYY-MM-DD');
  const locationId = ticketLocation?.id;
  useEffect(() => {
    let cancelled = false;
    if (!locationId || !visitDate) {
      setSlotsInfo({});
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchSlots({ id: locationId }, dayjs(visitDate), excludeSessionId).then(times => {
      if (!cancelled) setSlotsInfo(times);
    }).catch(() => {
      if (!cancelled) setSlotsInfo({});
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [visitDate, locationId, excludeSessionId]);
  return { slotsInfo, isLoading };
};

async function fetchSlots(ticketLocation, startDate, excludeSessionId) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(process.env.REACT_APP_FN_URL + '/getSlotsInfo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({ ticketLocation: ticketLocation.id, startDate, visitDate: startDate.format('YYYY-MM-DD'), excludeSessionId, userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
    });
    const data = await response.json();
    if (!response.ok || !data?.times || typeof data.times !== 'object') throw new Error('booking_check_unavailable');
    return data.times;
  } finally { clearTimeout(timer); }
}

export const checkSlotAvailable = async (ticketLocation, startDate, selectedTime, quantity, { excludeSessionId } = {}) => {
  // The server applies the venue clock; the browser may be in another timezone.
  const times = await fetchSlots(ticketLocation, startDate, excludeSessionId);
  const slot = times[selectedTime] || { groups: 0, groupSize: 0 };
  return slot.groups + 1 <= defaultMaxGroups && slot.groupSize + quantity <= (defaultMaxPeoplePerLocation[ticketLocation.id] || defaultMaxPeople);
};

export default TimeSlotSelector;
